import { Link } from 'react-router-dom';
import { countOf, fmtClaim, fmtMoney, type GbDebrief, type GbSeatView } from './types.js';

// ---------------------------------------------------------------------------
// Post-game debrief
// ---------------------------------------------------------------------------
// Everything, now that the anonymity mode has expired: outcome and
// payouts, every claim against every true value across the whole game,
// which seats were bots and what they really intended, and — in
// three-ball mode — what the alternatives would have paid.
//
// The four undrawn round-two balls are not here. They are never shown.

export function Debrief({
  debrief,
  seats,
  ballMode
}: {
  debrief: GbDebrief;
  seats: GbSeatView[];
  ballMode: 'twoBall' | 'threeBall';
}) {
  const label = (seatId: string) =>
    seats.find((seat) => seat.seatId === seatId)?.label ?? seatId;
  const botBySeat = new Map(debrief.bots.map((bot) => [bot.seatId, bot.finalIntent]));

  // Every claim, filed under the seat that made it. It used to be one
  // flat table sorted by round, underneath a per-seat block that gave
  // counts only — so "what did Cedar actually have?" meant scrolling a
  // seventeen-row list and picking their rows out by eye.
  const claimsBySeat = new Map<string, GbDebrief['claimAudit']>();
  for (const entry of debrief.claimAudit) {
    claimsBySeat.set(entry.seatId, [...(claimsBySeat.get(entry.seatId) ?? []), entry]);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
      <section className="shrink-0 rounded-lg border border-ec-brass/50 bg-ec-brass/5 p-3">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-ec-brass">
          Outcome
        </h3>
        <p className="font-serif text-sm text-ec-face">
          {debrief.finalResult?.explanation ?? 'No final was played.'}
        </p>
      </section>

      {/* One card per seat, side by side on anything wider than a phone.
          A four-seat game filled a single narrow column with a name on
          the left and a payout on the far right and nothing in between. */}
      <div className="grid shrink-0 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {debrief.perSeat.map((result) => {
          const identity = debrief.identities[result.seatId];
          const intent = botBySeat.get(result.seatId);
          const claims = claimsBySeat.get(result.seatId) ?? [];
          const analysis = debrief.finalAnalysis?.[result.seatId];
          return (
            <section
              key={result.seatId}
              className="flex flex-col gap-2 rounded-lg border border-ec-line bg-ec-panel p-2.5"
            >
              <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-display text-sm uppercase tracking-wide text-ec-face">
                  {result.label}
                </span>
                {result.isBot ? (
                  <span
                    title="What they were committed to, regardless of what they did"
                    className={`rounded-full border px-1.5 font-display text-[0.55rem] uppercase tracking-wide ${
                      intent === 'STEAL'
                        ? 'border-[#e02a1f]/60 text-[#e02a1f]'
                        : intent === 'SPLIT'
                          ? 'border-ec-brass/60 text-ec-brass'
                          : 'border-ec-line text-ec-bone-dim'
                    }`}
                  >
                    {intent === 'STEAL'
                      ? 'committed stealer'
                      : intent === 'SPLIT'
                        ? 'committed splitter'
                        : 'adaptive'}
                  </span>
                ) : (
                  identity && (
                    <Link
                      to={`/users/${identity}`}
                      className="truncate font-display text-[0.6rem] uppercase tracking-wide text-ec-brass underline-offset-2 hover:underline"
                    >
                      {identity}
                    </Link>
                  )
                )}
                <span className="ml-auto font-serif text-base tabular-nums text-ec-brass">
                  {fmtMoney(result.payout)}
                </span>
              </header>

              <p className="font-serif text-[0.65rem] text-ec-bone-dim">
                {result.reachedFinal
                  ? `final · ${result.finalChoice ?? '—'}`
                  : `out in round ${result.eliminatedRound}`}
                {result.finalByTimeout && ' (timed out)'} ·{' '}
                <span className="text-ec-go">{result.claimsTrue} true</span> ·{' '}
                <span className="text-ec-oxblood-2">{countOf(result.claimsLied, 'lie')}</span>
                {result.intentStated > 0 &&
                  ` · kept their word ${result.intentMatched}/${result.intentStated}`}
                {result.timeouts > 0 && ` · ${countOf(result.timeouts, 'timeout')}`}
              </p>

              {/* EVERY hidden ball this seat declared, claimed against
                  what it really was. This is the thing the whole game is
                  played to find out. */}
              <table className="w-full text-left font-serif text-xs">
                <thead>
                  <tr className="font-display text-[0.5rem] uppercase tracking-wide text-ec-bone-dim">
                    <th className="pb-0.5 font-normal">Rd</th>
                    <th className="pb-0.5 font-normal">Claimed</th>
                    <th className="pb-0.5 font-normal">Really</th>
                    <th className="pb-0.5 text-right font-normal">·</th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((entry) => (
                    <tr
                      key={`${entry.round}-${entry.ballId}`}
                      className="border-t border-ec-line/40"
                    >
                      <td className="py-0.5 text-ec-bone-dim">{entry.round}</td>
                      <td className="py-0.5 tabular-nums text-ec-bone">
                        {fmtClaim(entry.claimed)}
                      </td>
                      <td className="py-0.5 tabular-nums text-ec-face">
                        {fmtClaim(entry.actual)}
                      </td>
                      <td className="py-0.5 text-right">
                        <span
                          className={`font-display text-[0.55rem] uppercase tracking-wide ${
                            entry.honest ? 'text-ec-go' : 'text-ec-oxblood-2'
                          }`}
                        >
                          {entry.honest ? 'true' : 'lie'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {claims.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-0.5 text-ec-bone-dim">
                        Never declared a hidden ball.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {ballMode === 'threeBall' && analysis && (
                <div className="border-t border-ec-line/60 pt-1.5">
                  <p className="font-display text-[0.55rem] uppercase tracking-wide text-ec-bone-dim">
                    chose {analysis.chosen}
                    {analysis.optimal ? (
                      <span className="ml-1 text-ec-go">· best available</span>
                    ) : (
                      <span className="ml-1 text-ec-oxblood-2">· left money behind</span>
                    )}
                  </p>
                  <ul className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(analysis.payoutByChoice).map(([choice, payout]) => (
                      <li
                        key={choice}
                        className={`rounded border px-1.5 font-serif text-[0.65rem] tabular-nums ${
                          choice === analysis.chosen
                            ? 'border-ec-brass/60 text-ec-brass'
                            : 'border-ec-line text-ec-bone-dim'
                        }`}
                      >
                        {choice} {fmtMoney(payout)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
